import { useEffect, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import * as SliderPrimitive from '@radix-ui/react-slider';
import { cn } from '@/lib/utils';

// قسم قابل للطي (زي أمازون): عنوان + سهم، مفتوح افتراضياً
const FilterSection = ({ title, count = 0, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-200/80 dark:border-white/10 py-4 first:pt-0 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-2 text-right group"
      >
        <span className="font-tajawal font-bold text-[15px] text-brand-black flex items-center gap-2">
          {title}
          {count > 0 && (
            <span className="min-w-5 h-5 px-1.5 rounded-full bg-burgundy-500 text-white text-[11px] font-bold inline-flex items-center justify-center">
              {count}
            </span>
          )}
        </span>
        <ChevronDown
          className={cn('w-4 h-4 text-gray-400 transition-transform duration-200 group-hover:text-brand-black', open && 'rotate-180')}
        />
      </button>
      <div className={cn('grid transition-all duration-200', open ? 'grid-rows-[1fr] opacity-100 mt-3' : 'grid-rows-[0fr] opacity-0')}>
        <div className="overflow-hidden">{children}</div>
      </div>
    </div>
  );
};

// قائمة طويلة: أول 6 عناصر + "عرض المزيد"
const ShowMoreList = ({ items, limit = 6, render }) => {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, limit);
  return (
    <>
      {visible.map(render)}
      {items.length > limit && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-1 text-sm font-cairo font-semibold text-burgundy-500 hover:underline"
        >
          {expanded ? 'عرض أقل' : `عرض المزيد (${items.length - limit})`}
        </button>
      )}
    </>
  );
};

const PriceFilter = ({ bounds, minPrice, maxPrice, onChange }) => {
  const [range, setRange] = useState([minPrice ?? bounds.min, maxPrice ?? bounds.max]);

  // لما الفلاتر تتمسح من برّه نرجّع السلايدر لقيمه
  useEffect(() => {
    setRange([minPrice ?? bounds.min, maxPrice ?? bounds.max]);
  }, [minPrice, maxPrice, bounds.min, bounds.max]);

  const commit = ([lo, hi]) => {
    onChange(lo <= bounds.min ? null : lo, hi >= bounds.max ? null : hi);
  };

  const handleInput = (index) => (e) => {
    const next = [...range];
    next[index] = e.target.value === '' ? (index === 0 ? bounds.min : bounds.max) : Number(e.target.value);
    setRange(next);
  };

  const handleInputCommit = () => {
    const lo = Math.max(bounds.min, Math.min(range[0], range[1]));
    const hi = Math.min(bounds.max, Math.max(range[0], range[1]));
    setRange([lo, hi]);
    commit([lo, hi]);
  };

  if (bounds.max <= bounds.min) return null;

  return (
    <div className="px-1">
      <SliderPrimitive.Root
        dir="rtl"
        min={bounds.min}
        max={bounds.max}
        step={1}
        minStepsBetweenThumbs={1}
        value={range}
        onValueChange={setRange}
        onValueCommit={commit}
        className="relative flex w-full touch-none select-none items-center h-5"
        aria-label="نطاق السعر"
      >
        <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-gray-200 dark:bg-white/15">
          <SliderPrimitive.Range className="absolute h-full bg-burgundy-500" />
        </SliderPrimitive.Track>
        {[0, 1].map((i) => (
          <SliderPrimitive.Thumb
            key={i}
            aria-label={i === 0 ? 'أقل سعر' : 'أعلى سعر'}
            className="block h-5 w-5 rounded-full border-2 border-burgundy-500 bg-white shadow-md transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-burgundy-500/20"
          />
        ))}
      </SliderPrimitive.Root>

      <div className="mt-4 flex items-center gap-2">
        {[0, 1].map((i) => (
          <label key={i} className="flex-1">
            <span className="block text-[11px] font-cairo text-gray-500 mb-1">{i === 0 ? 'من (د.أ)' : 'إلى (د.أ)'}</span>
            <input
              type="number"
              inputMode="numeric"
              min={bounds.min}
              max={bounds.max}
              value={range[i]}
              onChange={handleInput(i)}
              onBlur={handleInputCommit}
              onKeyDown={(e) => e.key === 'Enter' && handleInputCommit()}
              className="w-full h-9 rounded-lg border border-gray-200 bg-white dark:bg-white/5 dark:border-white/15 px-2 text-sm font-cairo text-center focus:outline-none focus:border-burgundy-500 focus:ring-2 focus:ring-burgundy-500/15"
            />
          </label>
        ))}
      </div>
    </div>
  );
};

const FilterPanel = ({ options, filters, onToggleCategory, onToggleValue, onPriceChange, onToggleFlag }) => {
  if (!options) {
    return (
      <div className="space-y-6 animate-pulse" aria-busy="true">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="space-y-3">
            <div className="h-4 w-24 bg-gray-200 rounded" />
            <div className="h-3 w-full bg-gray-100 rounded" />
            <div className="h-3 w-4/5 bg-gray-100 rounded" />
            <div className="h-3 w-3/5 bg-gray-100 rounded" />
          </div>
        ))}
      </div>
    );
  }

  const selectedValues = new Set(filters.values);
  const selectedCategories = new Set(filters.categories);

  return (
    <div>
      {options.categories.length > 0 && (
        <FilterSection title="القسم" count={filters.categories.length}>
          <div className="space-y-1">
            <ShowMoreList
              items={options.categories}
              render={(category) => (
                <label
                  key={category.id}
                  className="flex items-center gap-3 py-1.5 px-2 -mx-2 rounded-lg cursor-pointer hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                >
                  <Checkbox
                    checked={selectedCategories.has(category.id)}
                    onCheckedChange={() => onToggleCategory(category.id)}
                    className="h-[18px] w-[18px] rounded-[5px] border-gray-300 data-[state=checked]:bg-burgundy-500 data-[state=checked]:border-burgundy-500 [&_svg]:h-3.5 [&_svg]:w-3.5"
                  />
                  <span className="flex-1 font-cairo text-sm text-brand-black">{category.name}</span>
                  <span className="text-xs font-cairo text-gray-400 tabular-nums">{category.count}</span>
                </label>
              )}
            />
          </div>
        </FilterSection>
      )}

      <FilterSection title="السعر" count={filters.minPrice != null || filters.maxPrice != null ? 1 : 0}>
        <PriceFilter
          bounds={options.price}
          minPrice={filters.minPrice}
          maxPrice={filters.maxPrice}
          onChange={onPriceChange}
        />
      </FilterSection>

      {options.attributes.map((attribute) => {
        const activeCount = attribute.values.filter((v) => selectedValues.has(v.id)).length;
        return (
          <FilterSection key={attribute.id} title={attribute.name} count={activeCount}>
            {attribute.is_color ? (
              <div className="grid grid-cols-5 gap-x-2 gap-y-3">
                {attribute.values.map((value) => {
                  const active = selectedValues.has(value.id);
                  return (
                    <button
                      key={value.id}
                      type="button"
                      onClick={() => onToggleValue(value.id)}
                      aria-pressed={active}
                      title={`${value.value} (${value.count})`}
                      className="flex flex-col items-center gap-1.5 group"
                    >
                      <span
                        className={cn(
                          'relative w-8 h-8 rounded-full ring-offset-2 ring-offset-white dark:ring-offset-gray-900 transition-all duration-200 shadow-sm',
                          active ? 'ring-2 ring-burgundy-500 scale-105' : 'ring-1 ring-black/10 group-hover:ring-black/30 group-hover:scale-105'
                        )}
                        style={{ backgroundColor: value.hex || '#E5E7EB' }}
                      >
                        {active && (
                          <Check
                            className={cn(
                              'absolute inset-0 m-auto w-4 h-4 drop-shadow',
                              isLight(value.hex) ? 'text-brand-black' : 'text-white'
                            )}
                            strokeWidth={3}
                          />
                        )}
                      </span>
                      <span className={cn('text-[11px] font-cairo leading-tight truncate max-w-full', active ? 'text-burgundy-500 font-bold' : 'text-gray-500')}>
                        {value.value}
                      </span>
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {attribute.values.map((value) => {
                  const active = selectedValues.has(value.id);
                  return (
                    <button
                      key={value.id}
                      type="button"
                      onClick={() => onToggleValue(value.id)}
                      aria-pressed={active}
                      className={cn(
                        'px-3 h-8 rounded-full border text-xs font-cairo font-semibold transition-all duration-200',
                        active
                          ? 'bg-burgundy-500 border-burgundy-500 text-white shadow-sm'
                          : 'bg-white dark:bg-white/5 border-gray-200 dark:border-white/15 text-gray-700 dark:text-gray-200 hover:border-burgundy-500 hover:text-burgundy-500'
                      )}
                    >
                      {value.value}
                      <span className={cn('mr-1 tabular-nums', active ? 'text-white/75' : 'text-gray-400')}>({value.count})</span>
                    </button>
                  );
                })}
              </div>
            )}
          </FilterSection>
        );
      })}

      <FilterSection title="العروض" count={filters.onSale ? 1 : 0}>
        <div className="space-y-3">
          {[
            { key: 'onSale', label: 'عليها خصم' },
          ].map(({ key, label }) => (
            <label key={key} className="flex items-center justify-between gap-3 cursor-pointer">
              <span className="font-cairo text-sm text-brand-black">{label}</span>
              <Switch
                checked={filters[key]}
                onCheckedChange={() => onToggleFlag(key)}
                className="data-[state=checked]:bg-burgundy-500"
              />
            </label>
          ))}
        </div>
      </FilterSection>
    </div>
  );
};

// هل اللون فاتح؟ (عشان علامة الصح تبان عليه)
function isLight(hex) {
  if (!hex || !/^#?[0-9a-f]{6}$/i.test(hex)) return true;
  const n = parseInt(hex.replace('#', ''), 16);
  const r = (n >> 16) & 255, g = (n >> 8) & 255, b = n & 255;
  return (r * 299 + g * 587 + b * 114) / 1000 > 160;
}

export default FilterPanel;
